import React, { useEffect, useState } from "react";
import { api, formatMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flame, RefreshCcw, Boxes } from "lucide-react";

export default function StockView() {
  const [stock, setStock] = useState({});
  const [bullet, setBullet] = useState({});
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, t] = await Promise.all([
        api.get("/stock/live"),
        api.get("/stock/bullet"),
        api.get("/totals/qty"),
      ]);
      setStock(s.data || {});
      setBullet(b.data || {});
      setTotals(t.data || null);
      setUpdatedAt(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const godowns = Object.keys(stock);

  return (
    <div className="space-y-6" data-testid="stock-view-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Boxes size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Live Stock</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Read-only view · updated {updatedAt ? updatedAt.toLocaleTimeString("en-IN") : "—"}
        </span>
        <button onClick={load} disabled={loading} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="stockview-refresh">
          <RefreshCcw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Totals KPIs */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-flat p-5 bg-black text-white border-black">
            <div className="small-label text-white/70">Total Filled</div>
            <div className="data-num text-3xl mt-1" data-testid="sv-filled">{formatMoney(totals.filled_total || 0)}</div>
            <div className="text-xs text-white/70 mt-1">cylinders across all godowns</div>
          </div>
          <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
            <div className="small-label text-[#B45309]">Total MT</div>
            <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="sv-mt">{formatMoney(totals.mt_total || 0)}</div>
            <div className="text-xs text-[#B45309]/80 mt-1">empty cylinders</div>
          </div>
          <div className="card-flat p-5">
            <div className="small-label text-gray-500">Defective</div>
            <div className="data-num text-3xl mt-1">{formatMoney(totals.defective_total || 0)}</div>
          </div>
          <div className="card-flat p-5 bg-[#FFF7ED] border-[#FED7AA]">
            <div className="small-label text-[#B45309] flex items-center gap-1.5"><Flame size={12} /> Total KG (LPG)</div>
            <div className="data-num text-3xl mt-1 text-[#B45309]">{formatMoney(Math.round((totals.total_kg || 0) * 100) / 100)}</div>
            <div className="text-xs text-[#92400E] mt-1">filled × size + bullets</div>
          </div>
        </div>
      )}

      {/* Bullet stock */}
      {Object.keys(bullet).length > 0 && (
        <div className="card-flat">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="small-label text-gray-500">Bulk LPG tanks</div>
              <h3 className="font-heading font-black text-xl">Bullet Stock · KG</h3>
            </div>
            <Flame size={20} className="text-[#B45309]" />
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(bullet).map(([name, kg]) => {
              const v = Math.round(Number(kg || 0) * 100) / 100;
              const neg = v < 0;
              return (
                <div key={name} className={`card-flat p-4 ${neg ? "bg-[#FEE2E2] border-[#FECACA]" : "bg-[#FFF7ED] border-[#FED7AA]"}`} data-testid={`sv-bullet-${name}`}>
                  <div className={`small-label flex items-center gap-1.5 ${neg ? "text-[#B91C1C]" : "text-[#B45309]"}`}>
                    <Flame size={12} /> {name}
                  </div>
                  <div className={`data-num text-3xl font-black mt-1 ${neg ? "text-[#B91C1C]" : "text-[#B45309]"}`}>
                    {formatMoney(v)} <span className="text-sm font-medium text-gray-600">KG</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Godown grid */}
      {godowns.map((g) => {
        const brands = stock[g] || {};
        const brandNames = Object.keys(brands);
        return (
          <div key={g} className="card-flat">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="small-label text-gray-500">Godown</div>
                <h3 className="font-heading font-black text-xl">{g}</h3>
              </div>
              <Boxes size={20} className="text-gray-400" />
            </div>
            <div className="overflow-auto">
              <table className="table-dense w-full">
                <thead>
                  <tr>
                    <th className="text-left">Brand</th>
                    <th className="text-left">Size</th>
                    <th className="num">Filled</th>
                    <th className="num">MT</th>
                    <th className="num">Defective</th>
                    <th className="num">KG (Filled × Size)</th>
                  </tr>
                </thead>
                <tbody>
                  {brandNames.map((b) => Object.entries(brands[b] || {}).map(([sz, v]) => {
                    const sizeKgNum = parseFloat(String(sz).toLowerCase().replace("kg", "").trim()) || 0;
                    const totalKg = (v.filled || 0) * sizeKgNum;
                    return (
                      <tr key={`${b}-${sz}`} data-testid={`sv-row-${g}-${b}-${sz}`}>
                        <td className="text-sm font-semibold">{b}</td>
                        <td className="text-sm">{sz}</td>
                        <td className="num text-[#15803D] font-bold">{v.filled || 0}</td>
                        <td className="num text-[#B45309]">{v.mt || 0}</td>
                        <td className="num text-gray-500">{v.defective || 0}</td>
                        <td className="num data-num">{formatMoney(Math.round(totalKg * 100) / 100)}</td>
                      </tr>
                    );
                  }))}
                  {brandNames.length === 0 && (
                    <tr><td colSpan="6" className="py-6 text-center text-gray-400 text-sm">No stock recorded in this godown yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {godowns.length === 0 && !loading && (
        <div className="card-flat p-8 text-center text-gray-400">
          <Boxes size={32} className="mx-auto mb-2 text-gray-300" />
          No stock data yet.
        </div>
      )}
    </div>
  );
}
